import { useState, useRef } from "react";
import { useTheme } from "../../../theme";

export default function ProjectCard({ project, index = 0 }) {
  const t = useTheme();
  const [hover, setHover] = useState(false);
  const [pos, setPos] = useState({ x: 0.5, y: 0.5 });
  const cardRef = useRef(null);
  const isClickable = Boolean(project.href);

  const handleMove = (e) => {
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPos({
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    });
  };

  const handleLeave = () => {
    setHover(false);
    // Ease the tilt back to neutral
    setPos({ x: 0.5, y: 0.5 });
  };

  const handleClick = () => {
    if (project.href) window.open(project.href, "_blank", "noopener,noreferrer");
  };

  // ±7 degrees tilt around the card center, clamped gently
  const tiltX = hover ? (0.5 - pos.y) * 14 : 0;
  const tiltY = hover ? (pos.x - 0.5) * 14 : 0;

  return (
    <div
      ref={cardRef}
      role={isClickable ? "link" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={`${project.name} — ${project.tagline}`}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (isClickable && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          handleClick();
        }
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={handleLeave}
      onMouseMove={handleMove}
      style={{
        position: "relative",
        background: t.surface,
        border: `1px solid ${hover ? project.color + "66" : t.border}`,
        borderRadius: 22,
        overflow: "hidden",
        transition: hover
          ? "transform 90ms ease-out, border-color 400ms ease, box-shadow 400ms ease"
          : "transform 700ms cubic-bezier(0.22, 1, 0.36, 1), border-color 400ms ease, box-shadow 700ms cubic-bezier(0.22, 1, 0.36, 1)",
        cursor: isClickable ? "pointer" : "default",
        transform: `perspective(1400px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) translateY(${hover ? -10 : 0}px)`,
        transformStyle: "preserve-3d",
        boxShadow: hover
          ? `0 40px 80px -24px ${project.color}55, 0 20px 48px -16px rgba(0,0,0,0.55), 0 0 0 1px ${project.color}22 inset`
          : `0 14px 40px -18px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.02) inset`,
        display: "flex",
        flexDirection: "column",
        outline: "none",
        willChange: "transform",
      }}
    >
      {/* Cursor-follow radial highlight */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 3,
          opacity: hover ? 1 : 0,
          transition: "opacity 400ms ease",
          background: `radial-gradient(420px circle at ${pos.x * 100}% ${pos.y * 100}%, ${project.color}26, transparent 55%)`,
          mixBlendMode: "screen",
        }}
      />

      {/* Hero — logo / image parallax */}
      <div
        style={{
          aspectRatio: "16 / 10",
          background: project.image
            ? `${project.bg || "transparent"}, url(${project.image}) center/cover no-repeat`
            : project.bg || `linear-gradient(135deg, ${project.color}33, ${project.color}11)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          borderBottom: `1px solid ${t.borderSubtle}`,
          transformStyle: "preserve-3d",
        }}
      >
        {project.logo && !project.image && (
          <img
            src={project.logo}
            alt={`${project.name} logo`}
            style={{
              width: project.logoSize || 120,
              height: project.logoSize || 120,
              objectFit: "contain",
              filter: hover
                ? `drop-shadow(0 14px 32px ${project.color}66)`
                : "drop-shadow(0 4px 12px rgba(0,0,0,0.3))",
              transition: "filter 500ms ease, transform 500ms cubic-bezier(0.22, 1, 0.36, 1)",
              transform: hover ? "scale(1.08) translateZ(50px)" : "scale(1) translateZ(0)",
              transformStyle: "preserve-3d",
            }}
          />
        )}
        {!project.logo && !project.image && project.icon && (
          <span
            style={{
              fontSize: 96,
              color: project.color,
              opacity: 0.62,
              fontWeight: 200,
              transition: "transform 500ms cubic-bezier(0.22, 1, 0.36, 1), filter 400ms ease",
              transform: hover
                ? "scale(1.1) rotate(-5deg) translateZ(50px)"
                : "scale(1) rotate(0) translateZ(0)",
              filter: hover ? `drop-shadow(0 14px 32px ${project.color}66)` : "none",
              transformStyle: "preserve-3d",
            }}
          >
            {project.icon}
          </span>
        )}
        {/* "Live" tag for clickable projects */}
        {isClickable && (
          <span
            style={{
              position: "absolute",
              top: 16,
              right: 16,
              padding: "6px 12px",
              borderRadius: 100,
              background: "rgba(0,0,0,0.5)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              color: "#fff",
              fontSize: 10,
              fontFamily: "'DM Mono', monospace",
              letterSpacing: 1.5,
              textTransform: "uppercase",
              opacity: hover ? 1 : 0.65,
              transform: hover ? "translateZ(30px)" : "translateZ(0)",
              transition: "opacity 300ms, transform 500ms cubic-bezier(0.22, 1, 0.36, 1)",
              transformStyle: "preserve-3d",
            }}
          >
            ↗ Live
          </span>
        )}
      </div>

      {/* Body */}
      <div
        style={{
          padding: 28,
          display: "flex",
          flexDirection: "column",
          gap: 12,
          flex: 1,
          position: "relative",
          zIndex: 2,
          transformStyle: "preserve-3d",
        }}
      >
        <span
          style={{
            color: project.color,
            fontSize: 11,
            fontFamily: "'DM Mono', monospace",
            letterSpacing: 2,
            textTransform: "uppercase",
            opacity: 0.92,
            transform: hover ? "translateZ(20px)" : "translateZ(0)",
            transition: "transform 500ms cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        >
          {project.tagline}
        </span>
        <h4
          style={{
            fontSize: 24,
            fontWeight: 800,
            color: t.text,
            margin: 0,
            letterSpacing: -0.6,
            lineHeight: 1.15,
            transform: hover ? "translateZ(30px)" : "translateZ(0)",
            transition: "transform 500ms cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        >
          {project.name}
        </h4>
        <p
          style={{
            color: t.textMid,
            fontSize: 14,
            lineHeight: 1.7,
            fontFamily: "Georgia, serif",
            margin: 0,
            transform: hover ? "translateZ(15px)" : "translateZ(0)",
            transition: "transform 500ms cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        >
          {project.description}
        </p>
      </div>

      {/* Bottom accent bar — only visible on hover */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: 24,
          right: 24,
          bottom: 0,
          height: 2,
          borderRadius: 2,
          background: `linear-gradient(90deg, transparent, ${project.color}, transparent)`,
          opacity: hover ? 0.9 : 0,
          transform: hover ? "scaleX(1)" : "scaleX(0.3)",
          transformOrigin: "center",
          transition: "opacity 400ms ease, transform 600ms cubic-bezier(0.22, 1, 0.36, 1)",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}

